import { setTitle } from 'Utils';
import emptyDashTemplate from './components/dashboard/empty_dashboard.html';

/* @ngInject */
function RoutesConfig(
  $locationProvider,
  $stateProvider,
  $urlRouterProvider,
  $urlMatcherFactoryProvider,
  $transitionsProvider,
) {
  // If the user enters a URL that doesn't match any known URL (state), send them to …
  $urlRouterProvider.otherwise('/error');
  $locationProvider.html5Mode(true).hashPrefix('');
  // Disable strict mode to ignore trailing slashes www.app.ptc.com/dashboard/
  // https://github.com/angular-ui/ui-router/wiki/Frequently-Asked-Questions
  $urlMatcherFactoryProvider.strictMode(false);

  const appState = {
    name: 'app',
    url: '/',
    component: 'siteselect'
    // TODO: https://ui-router.github.io/guide/views#defining-named-views
  };

  const dashboardState = {
    name: 'dashboard',
    url: '/dashboard/{id}',
    component: 'dashboardComponent',
    resolve: {
      resolvedEqs($state, $stateParams, DashboardInfoService, DashboardService, EquipmentInfoService) {
        'ngInject';

        const { id } = $stateParams;
        if (id === 'new') {
          return;
        }

        return DashboardInfoService.getAllDashboards()
          .then(() => {
            if (!DashboardInfoService.getDashboard(id)) {
              setTimeout(() => $state.go('error')); // Throws transition error if not executed outside
              return;
            }

            EquipmentInfoService.setNeed('dashboard');
            const equipment = DashboardInfoService.getEquipment(DashboardService.getCurrentDashboard());
            return _.isEmpty(equipment) ? equipment : EquipmentInfoService.getEquipmentById(equipment);
          });
      }
    },
  };

  const emptyDashboardState = {
    name: 'empty dashboard',
    url: '/dashboard',
    template: emptyDashTemplate
  };

  const errorState = {
    name: 'error',
    url: '/error',
    template: `
      <div class="row">
          <div class="col-md-4 col-md-offset-4 col-sm-6 col-sm-offset-3 col-xs-offset-1 col-xs-10">
              <div class="error-container">
                  <div class="error-main">
                      <h1 style="font-size: 8em;"> Oops.</h1>
                      <h3> The page you are trying to reach doesn't exist. </h3>
                      <a href="/" class="btn btn-dark">Go back to the Home page</a>
                  </div>
              </div>
          </div>
      </div>`
  };

  const profileState = {
    name: 'user profile',
    url: '/profile/user',
    component: 'profileComponent'
  };

  // Admin Routes
  const adminStates = [
    {
      name: 'admin',
      url: '/admin',
      template: '<div ui-view id="admin-viewport"></div>'
    },
    {
      name: 'admin.dataUsage',
      url: '/dataUsage',
      component: 'adminDataUsageComponent'
    },
    {
      name: 'admin.equipment',
      url: '/equipment',
      component: 'adminEquipmentComponent',
      resolve: {
        resolves(growl, EquipmentInfoService, RcuInfoService) {
          'ngInject';

          // TODO: Make use of these resolves as bindings
          EquipmentInfoService.setNeed('admin_equipment');
          const { destroy } = growl.info('We appreciate your patience as this can take awhile...', {
            title: 'Retrieving ALL equipment'
          });
          return Promise.all([
            EquipmentInfoService.getAllEquipments(),
            RcuInfoService.getAllRcus(),
          ]).finally(destroy);
        },
        allOrgs(Organization) {
          'ngInject';

          return (async () => Organization.model)();
        }
      },
    },
    {
      name: 'admin.equipmentManagement',
      url: '/equipmentManagement',
      component: 'equipmentTableComponent',
    },
    {
      name: 'admin.equipment.new',
      url: '/new',
    },
    {
      name: 'admin.equipment.derp',
      url: '/edit',
      redirectTo: 'admin.equipment'
    },
    {
      // ? This is just a convenient shortcut
      name: 'equipment',
      url: '/equipment/:id',
      redirectTo: 'admin.equipment.eq'
    },
    {
      name: 'admin.equipment.eq',
      url: '/:id',
    },
    {
      name: 'admin.equipment.edit',
      url: '/edit/:id',
    },
    {
      name: 'admin.equipmentNotifications',
      url: '/equipmentNotifications',
      component: 'equipmentNotificationsComponent'
    },
    {
      name: 'admin.equipmentNotifications.eq',
      url: '/:id',
      // TODO: Maybe we can do something with this since its a dupe of alerts (by default)
    },
    {
      name: 'admin.equipmentNotifications.alerts',
      url: '/:id/alerts',
    },
    {
      name: 'admin.equipmentNotifications.schedule',
      url: '/:id/schedule',
    },
    {
      name: 'admin.equipmentNotifications.logs',
      url: '/:id/logs',
    },
    {
      name: 'admin.userAccessLists',
      url: '/userAccessLists',
      component: 'userAccessLists'
    },
    {
      name: 'admin.groups',
      url: '/groups',
      component: 'groupComponent',
    },
    {
      name: 'admin.groups.group',
      url: '/:id',
    },
    {
      name: 'admin.groups.new',
      url: '/new'
      // TODO: Implement
    },
    {
      name: 'admin.groups.edit',
      url: '/edit/:id'
      // TODO: Implement
    },
    {
      name: 'admin.liveMonitoring',
      url: '/liveMonitoring',
      component: 'liveMonitoringComponent'
    },
    {
      name: 'admin.monitoring',
      url: '/monitoring',
      component: 'monitoringComponent'
    },
    {
      name: 'admin.videoMonitoring',
      url: '/videoMonitoring',
      component: 'videoMonitoringComponent'
    },
    {
      name: 'admin.rcuManagement',
      url: '/rcuManagement',
      component: 'rcuManagementComponent'
    },
    {
      name: 'admin.sites',
      url: '/sites',
      component: 'siteManagement',
      resolve: {
        resolvedSites(SiteService) {
          'ngInject';

          return SiteService.get();
        }
      },
    },
    {
      name: 'admin.sites.site',
      url: '/:id',
    },
    {
      name: 'admin.sites.new',
      url: '/new',
    },
    {
      name: 'admin.sites.edit',
      url: '/edit/:id'
    },
    {
      name: 'admin.types',
      url: '/types',
      component: 'typeManagement',
    },
    {
      name: 'admin.types.type',
      url: '/:typeId',
    },
    {
      name: 'admin.types.new',
      url: '/new',
    },
    {
      name: 'admin.types.edit',
      url: '/edit/:typeId'
    },
    {
      name: 'admin.resources',
      url: '/resources',
      component: 'resourceManagement',
    },
    {
      name: 'admin.resources.resource',
      url: '/:resourceId',
    },
    {
      name: 'admin.resources.new',
      url: '/new',
    },
    {
      name: 'admin.resources.resource.edit',
      url: '/edit'
    },
    {
      name: 'admin.users',
      url: '/users',
      component: 'usersComponent'
    },
    {
      // Technically part of the admin section
      name: 'user log',
      url: '/user/log/{id}',
      component: 'userLogComponent'
    },
    {
      name: 'admin.organizations',
      url: '/organizations',
      component: 'organizations',
      resolve: {
        organizations(Organization) {
          'ngInject';

          return (async () => Organization.model)();
        },
      }
    },
    {
      name: 'admin.organizations.new',
      url: '/new',
    },
    {
      name: 'admin.organizations.org',
      url: '/:id',
      resolve: {
        org($stateParams, Organization) {
          'ngInject';

          return Organization.get($stateParams.id);
        }
      }
    },
    {
      name: 'admin.organizations.org.edit',
      url: '/edit',
    },
    {
      name: 'admin.organizations.org.settings',
      url: '/settings',
    },
    {
      name: 'admin.organizations.org.payment',
      url: '/payment',
    },
    {
      name: 'admin.organizations.org.licenses',
      url: '/licenses',
    },
    {
      name: 'admin.organizations.org.schedules',
      url: '/schedules/:schedule',
      params: {
        schedule: null // ? Optional
      }
    },
    {
      name: 'admin.rules',
      url: '/rules',
      component: 'rules'
    },
    {
      name: 'admin.lprManagement',
      url: '/lprManagement',
      component: 'lprManagement'
    },
  ];

  // TODO: This route is a hack and unused in our app
  const eventDetailsState = {
    name: 'event details',
    url: '/event/{id}',
    component: 'eventDetailPageComponent'
  };

  const reportsState = {
    name: 'reports',
    url: '/reports/{type}',
    component: 'reports'
  };

  const internalErrorState = {
    name: 'internal error',
    url: '/500',
    template: `
      <div class="row">
        <div class="col-md-4 col-md-offset-4 col-sm-6 col-sm-offset-3 col-xs-offset-1 col-xs-10">
            <div class="error-container">
                <div class="error-main">
                    <h1> 500</h1>
                    <h3> It seems that we have an internal server error. </h3>
                    <h4>We have logged this issue and will be investigating it soon.</h4>
                    <br>
                    <a href="/" class="btn btn-dark">Go back to the Home page</a>
                </div>
            </div>
        </div>
      </div>`
  };

  [
    appState,
    ...adminStates,
    dashboardState,
    emptyDashboardState,
    profileState,
    eventDetailsState,
    reportsState,
    errorState,
    internalErrorState,
  ].forEach($stateProvider.state);

  // Update site title when transitioning between pages
  $transitionsProvider.onStart({}, (transition) => {
    const { name: route } = transition.to();
    const exemptions = [
      /dashboard/,
      // eslint-disable-next-line unicorn/no-unsafe-regex -- routes are short strings; not a concern
      /^admin\.equipment(\.\w+)?$/,
    ];
    _.some(exemptions, pattern => pattern.test(route)) || setTitle(route, true);
  });
}

export default angular
  .module('petroCloud.routing', [])
  .config(RoutesConfig)
  .name;
